import { fetchWithAuth } from '../utils/api';
import { config } from '../config';

/**
 * Signal Import Service
 * Handles all API interactions for async signal import functionality
 */

export interface SignalImportUploadResponse {
  session_id: string;
  file_name: string;
  file_size: number;
  detected_rows: number;
  message: string;
}

export interface SignalImportStatus {
  session_id: string;
  status: 'pending' | 'processing' | 'complete' | 'error' | 'cancelled';
  progress: {
    processed: number;
    total: number;
    percentage: number;
    current_batch: number;
    total_batches: number;
  };
  error?: string;
  file_info: {
    file_name: string;
    file_size: number;
    detected_rows: number;
  };
  started_at?: string;
  completed_at?: string;
}

export interface ImportedSignalsResponse {
  session_id: string;
  signals: Array<{
    signal_id: string;
    customer_name?: string;
    customer_email?: string;
    channel_type?: string;
    raw_text?: string;
    verbatim?: string;
    classification?: any;
    created_at: string;
  }>;
  total: number;
  page: number;
  limit: number;
}

/**
 * Upload a file for signal import
 * @param file The file to upload
 * @param datasetId The dataset ID to import signals into
 * @returns Promise with upload response including session ID
 */
export const uploadSignalFile = async (
  file: File,
  datasetId: string
): Promise<SignalImportUploadResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('dataset_id', datasetId);

  const response = await fetch(`${config.API_URL}/v1/signals/import/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: formData
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
    throw new Error(error.detail || 'Failed to upload file');
  }

  return await response.json();
};

/**
 * Start processing an uploaded file
 * @param sessionId The session ID from upload
 * @returns Promise with start response
 */
export const startSignalImport = async (sessionId: string): Promise<any> => {
  console.log('[SIGNAL-IMPORT-DEBUG] startSignalImport called with sessionId:', sessionId);
  const endpoint = `/v1/signals/import/${sessionId}/start`;
  console.log('[SIGNAL-IMPORT-DEBUG] API endpoint:', endpoint);
  
  try {
    const response = await fetchWithAuth(endpoint, {
      method: 'POST'
    });
    console.log('[SIGNAL-IMPORT-DEBUG] API response:', response);
    return response;
  } catch (error) {
    console.error('[SIGNAL-IMPORT-DEBUG] API call failed:', error);
    throw error;
  }
};

/**
 * Get the status of an import session
 * @param sessionId The session ID to check
 * @returns Promise with import status
 */
export const getImportStatus = async (sessionId: string): Promise<SignalImportStatus> => {
  return await fetchWithAuth(`/v1/signals/import/${sessionId}/status`);
};

/**
 * Cancel an active import session
 * @param sessionId The session ID to cancel
 * @returns Promise with cancellation response
 */
export const cancelSignalImport = async (sessionId: string): Promise<any> => {
  return await fetchWithAuth(`/v1/signals/import/${sessionId}/cancel`, {
    method: 'POST'
  });
};

/**
 * Get the imported signals for a session
 * @param sessionId The session ID
 * @param page Page number (default 1)
 * @param limit Items per page (default 100)
 * @returns Promise with imported signals
 */
export const getImportedSignals = async (
  sessionId: string, 
  page: number = 1, 
  limit: number = 100
): Promise<ImportedSignalsResponse> => {
  const params = new URLSearchParams({
    page: page.toString(),
    limit: limit.toString()
  });
  
  return await fetchWithAuth(`/v1/signals/import/${sessionId}/signals?${params}`);
};

/**
 * Delete an import session and its data
 * @param sessionId The session ID to delete
 * @returns Promise with deletion response
 */
export const deleteImportSession = async (sessionId: string): Promise<any> => {
  return await fetchWithAuth(`/v1/signals/import/${sessionId}`, {
    method: 'DELETE'
  });
};

/**
 * Get all import sessions for the current organization
 * @param status Optional status filter
 * @param limit Number of sessions to return (default 10)
 * @returns Promise with import sessions
 */
export const getImportSessions = async (
  status?: string,
  limit: number = 10
): Promise<Array<SignalImportStatus>> => {
  const params = new URLSearchParams({
    limit: limit.toString()
  });
  
  if (status) {
    params.append('status', status);
  }
  
  return await fetchWithAuth(`/v1/signals/import/sessions?${params}`);
};